import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Chip,
  Paper,
  Stack,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { formatDateField } from "../../../../../../base/FormatTime";
import {
  gdsRiskLevels,
  GDS_QUESTIONS,
} from "./geriatricDepressionScale.config";

const Section = ({ title, children }) => (
  <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5 }}>
    <Typography
      variant="subtitle1"
      component="h3"
      sx={{ fontWeight: "bold", mb: 2, color: "text.primary" }}
    >
      {title}
    </Typography>
    {children}
  </Paper>
);

const DetailItem = ({ label, value, isLast = false }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      py: 1.5,
      borderBottom: !isLast ? 1 : 0,
      borderColor: "divider",
    }}
  >
    <Typography variant="body2" sx={{ color: "text.secondary", flex: 1 }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, textAlign: "right" }}>
      <Typography
        variant="body2"
        sx={{ fontWeight: "medium", color: "text.primary" }}
      >
        {value || "N/A"}
      </Typography>
    </Box>
  </Box>
);

const getChoiceLabel = (value) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "Not answered";
};

const GeriatricDepressionScaleDetailsModal = ({
  open,
  handleClose,
  assessment,
}) => {
  if (!assessment) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div">
            Geriatric Depression Scale (GDS) Details
          </Typography>
          <IconButton onClick={handleClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: "grey.50", p: 3 }}>
        <Stack spacing={2}>
          <Section title="General Information">
            <DetailItem label="Service User" value={assessment.client_name} />
            <DetailItem
              label="Date of Assessment"
              value={formatDateField(assessment.created_at)}
            />
            <DetailItem label="Created By" value={assessment.created_by_name} />
            <DetailItem
              label="Care Plan"
              isLast
              value={assessment.care_plan_name}
            />
            <DetailItem
              label="Total Score"
              isLast
              value={`${assessment.score} / 15`}
            />
          </Section>

          <Section title="Assessment Details">
            {GDS_QUESTIONS.map((q, index) => (
              <DetailItem
                key={q.name}
                label={q.label}
                value={getChoiceLabel(assessment[q.name])}
                isLast={index === GDS_QUESTIONS.length - 1}
              />
            ))}
          </Section>

          <Section title="Comments">
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
            >
              {assessment.comments || "No comments provided."}
            </Typography>
          </Section>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
        <Button onClick={handleClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GeriatricDepressionScaleDetailsModal;
